import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useLocation } from 'react-router-dom';
import { Home, ArrowRight, Mail, Compass } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';

const QUICK_LINKS = [
  { to: '/',                label: 'Home',              desc: 'SALAR overview and capabilities at a glance.' },
  { to: '/capabilities',    label: 'Capabilities',      desc: 'Reliability, supply-chain, and provenance offerings.' },
  { to: '/resources',       label: 'Resources',         desc: 'Capability statements and public-safe materials.' },
  { to: '/news-updates',    label: 'News & Updates',    desc: 'Latest releases, demos, and announcements.' },
  { to: '/compliance',      label: 'Compliance',        desc: 'Public compliance-readiness statements.' },
  { to: '/contact',         label: 'Contact',           desc: 'Reach the SALAR team directly.' },
];

const NotFound = () => {
  const location = useLocation();

  return (
    <div className="min-h-screen flex flex-col" style={{ background: '#0A1628' }} data-testid="not-found-page">
      <Helmet>
        <title>Page Not Found | SALAR Technologies LLC</title>
        <meta name="description" content="The page you are looking for could not be found on the SALAR Technologies LLC website. Use the quick links to find what you need." />
        <meta name="robots" content="noindex, follow" />
        <link rel="canonical" href="https://www.salartechnologies.com/404" />
        <meta property="og:title" content="Page Not Found | SALAR Technologies LLC" />
        <meta property="og:description" content="The page you are looking for could not be found on the SALAR Technologies LLC website." />
        <meta property="og:url" content="https://www.salartechnologies.com/404" />
      </Helmet>

      <section className="flex-1 flex items-center justify-center py-20">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center text-white">
            <Badge variant="outline" className="border-cyan-400 text-cyan-300 mb-5 tracking-widest text-xs">
              404 &middot; PAGE NOT FOUND
            </Badge>
            <div className="text-7xl md:text-8xl font-bold text-cyan-300 leading-none mb-4" aria-hidden="true">
              404
            </div>
            <h1 className="text-3xl md:text-4xl font-bold mb-4 leading-tight">
              The page you are looking for could not be found.
            </h1>
            <p className="text-base md:text-lg text-blue-100 leading-relaxed mb-3 max-w-2xl mx-auto">
              The URL may have changed, the link may be outdated, or the page may have been moved.
              Use the quick links below to navigate to the right place.
            </p>
            {location?.pathname && (
              <p
                className="text-xs text-blue-300/70 italic mb-8 break-all"
                data-testid="not-found-attempted-path"
              >
                Attempted path: <code>{location.pathname}</code>
              </p>
            )}

            <div className="flex flex-col sm:flex-row gap-3 justify-center mb-10">
              <Link to="/" data-testid="not-found-home-btn">
                <Button size="lg" className="bg-cyan-600 hover:bg-cyan-700 text-white font-semibold">
                  <Home className="mr-2" size={18} />
                  Back to Home
                </Button>
              </Link>
              <Link to="/contact" data-testid="not-found-contact-btn">
                <Button
                  size="lg"
                  variant="outline"
                  className="border-cyan-300 text-cyan-200 hover:bg-cyan-300 hover:text-[#0a1628]"
                >
                  <Mail className="mr-2" size={16} />
                  Contact SALAR
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 border-t border-blue-800/30" style={{ background: '#0F1A2B' }}>
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-8">
              <Compass size={20} className="inline-block text-cyan-300 mr-2" aria-hidden="true" />
              <span className="text-cyan-300 text-xs font-bold tracking-widest uppercase">
                Quick Navigation
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {QUICK_LINKS.map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  className="block rounded-lg p-4 border border-cyan-500/25 transition-colors hover:border-cyan-300"
                  style={{ background: '#1A2744' }}
                  data-testid={`not-found-quick-${l.to.replace(/\W+/g, '-').replace(/^-+|-+$/g, '') || 'home'}`}
                >
                  <div className="flex items-center justify-between gap-3 mb-1.5">
                    <span className="text-base font-bold text-white">{l.label}</span>
                    <ArrowRight size={16} className="text-cyan-300 flex-shrink-0" />
                  </div>
                  <p className="text-xs text-blue-100/85 leading-relaxed">{l.desc}</p>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default NotFound;
